import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {EBirdMyDataSchema} from "ebird-mydata-reader";
import './big-year.scss';
import YouTube, {YouTubeProps} from "react-youtube";
import {PlayControls} from "../components/play-controls";

export type BigYearProps = {
    observations: EBirdMyDataSchemaWithImage[],
    year: string | number | undefined,
    setObservationImages: (observations: EBirdMyDataSchema[]) => EBirdMyDataSchemaWithImage[]
}

export type EBirdMyDataSchemaWithImage = EBirdMyDataSchema & {
    image?: string | undefined;
    speciesGroup?: string | undefined;
}

const VIDEO_ID = 'OlJeS_8jcc0';
const SOUNDTRACK_DURATION_MS = 210000;
const SHOW_START_DELAY_MS = 4500;
const SHOW_END_BUFFER_MS = 3000;
const MIN_PHOTO_REVEAL_MS = 240;
const SOUNDTRACK_RETRY_MS = 250;
const SOUNDTRACK_MAX_ATTEMPTS = 12;

export default function BigYear({observations, year, setObservationImages}: BigYearProps) {
    const rootEl = useRef<HTMLDivElement | null>(null);
    const imagesEl = useRef<HTMLDivElement | null>(null);
    const labelsEl = useRef<HTMLDivElement | null>(null);
    const player = useRef<any>(null);
    const animationFrame = useRef<number>();
    const revealTimer = useRef<number>();
    const soundtrackRetryTimer = useRef<number>();
    const currentOffset = useRef(0);
    const movementStartOffset = useRef(0);
    const movementEndOffset = useRef(0);
    const movementStartTime = useRef(0);
    const movementDuration = useRef(0);
    const photoRevealDuration = useRef(MIN_PHOTO_REVEAL_MS);
    const revealedIndex = useRef(0);
    const nextRevealIndex = useRef(0);
    const nextRevealDueAt = useRef(0);
    const remainingRevealDelay = useRef(0);
    const remainingMovementDelay = useRef(0);
    const revealImageRef = useRef<(imageIndex: number) => void>(() => {});
    const started = useRef(false);
    const paused = useRef(false);
    const complete = useRef(false);
    const [images, setImages] = useState<EBirdMyDataSchemaWithImage[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [loadedCount, setLoadedCount] = useState(0);
    const [totalImages, setTotalImages] = useState(0);
    const [isPlayerReady, setIsPlayerReady] = useState(false);
    const [labels, setLabels] = useState<[string, number][]>([]);
    const [imageOffsets, setImageOffsets] = useState<number[]>([]);

    const clearTimers = useCallback(() => {
        if (revealTimer.current) {
            window.clearTimeout(revealTimer.current);
            revealTimer.current = undefined;
        }

        if (animationFrame.current) {
            window.cancelAnimationFrame(animationFrame.current);
            animationFrame.current = undefined;
        }

        if (soundtrackRetryTimer.current) {
            window.clearTimeout(soundtrackRetryTimer.current);
            soundtrackRetryTimer.current = undefined;
        }
    }, []);

    const preloadImage = (observation: EBirdMyDataSchemaWithImage) => new Promise<EBirdMyDataSchemaWithImage | null>((resolve) => {
        if (!observation.image) {
            resolve(null);
            return;
        }

        const img = new Image();
        img.onload = () => resolve(observation);
        img.onerror = () => resolve(null);
        img.src = observation.image;
    });

    useEffect(() => {
        let isCancelled = false;
        clearTimers();
        started.current = false;
        currentOffset.current = 0;
        movementStartOffset.current = 0;
        movementEndOffset.current = 0;
        movementStartTime.current = 0;
        movementDuration.current = 0;
        photoRevealDuration.current = MIN_PHOTO_REVEAL_MS;
        revealedIndex.current = 0;
        nextRevealIndex.current = 0;
        nextRevealDueAt.current = 0;
        remainingRevealDelay.current = 0;
        remainingMovementDelay.current = 0;
        paused.current = false;
        complete.current = false;
        setCurrentIndex(0);
        setIsPaused(false);
        setImages([]);
        setLabels([]);
        setImageOffsets([]);
        setLoadedCount(0);
        setIsLoading(true);

        if (imagesEl.current) {
            imagesEl.current.style.transform = 'translate3d(0, 0, 0)';
        }

        if (labelsEl.current) {
            labelsEl.current.style.transform = 'translate3d(0, 0, 0)';
        }

        let tmpObs = setObservationImages(observations);
        tmpObs = tmpObs.sort((a, b) => {
            return a.taxonomicOrder < b.taxonomicOrder ? -1 : 1;
        }).filter(observation => observation.image);

        setTotalImages(tmpObs.length);

        Promise.all(tmpObs.map(observation => (
            preloadImage(observation).then((loadedObservation) => {
                if (!isCancelled) {
                    setLoadedCount((count) => count + 1);
                }

                return loadedObservation;
            })
        ))).then((loadedImages) => {
            if (isCancelled) {
                return;
            }

            const validImages = loadedImages.filter((observation): observation is EBirdMyDataSchemaWithImage => Boolean(observation));
            setImages(validImages);

            let labelMap: {[key: string]: number} = {};
            validImages.forEach((observation, i) => {
                if (observation.speciesGroup && !labelMap.hasOwnProperty(observation.speciesGroup)) {
                    labelMap[observation.speciesGroup] = i;
                }
            });

            const labelsArray = Object.entries(labelMap);
            labelsArray.sort((a, b) => {
                return a[1] < b[1] ? -1 : 1;
            });
            setLabels(labelsArray);
        });

        return function cleanup() {
            isCancelled = true;
            clearTimers();
        }
    }, [observations, setObservationImages, clearTimers]);

    useLayoutEffect(() => {
        if (!imagesEl.current || !images.length) {
            return;
        }

        const offsets = Array.from(imagesEl.current.children).map((child) => {
            return (child as HTMLElement).offsetTop;
        });

        const hasDistinctOffsets = offsets.some((offset, i) => i > 0 && offset !== offsets[0]);
        if (hasDistinctOffsets) {
            setImageOffsets(offsets);
            return;
        }

        const firstImage = imagesEl.current.children.item(0) as HTMLElement | null;
        const imageSpacing = firstImage ? firstImage.offsetHeight + parseFloat(getComputedStyle(firstImage).marginBottom) : 0;
        setImageOffsets(offsets.map((_, i) => i * imageSpacing));
    }, [images]);

    const applyStageOffset = useCallback((offset: number) => {
        currentOffset.current = offset;
        const nextTransform = `translate3d(0, -${offset}px, 0)`;

        if (imagesEl.current) {
            imagesEl.current.style.transform = nextTransform;
        }

        if (labelsEl.current) {
            labelsEl.current.style.transform = nextTransform;
        }
    }, []);

    const getCurrentMovementOffset = useCallback(() => {
        const elapsed = performance.now() - movementStartTime.current;
        const progress = movementDuration.current ? Math.min(Math.max(elapsed / movementDuration.current, 0), 1) : 0;
        return movementStartOffset.current + ((movementEndOffset.current - movementStartOffset.current) * progress);
    }, []);

    const movePhotos = useCallback(() => {
        if (paused.current) {
            return;
        }

        applyStageOffset(getCurrentMovementOffset());

        animationFrame.current = window.requestAnimationFrame(movePhotos);
    }, [applyStageOffset, getCurrentMovementOffset]);

    const startAnimationLoop = useCallback(() => {
        if (animationFrame.current) {
            window.cancelAnimationFrame(animationFrame.current);
        }

        animationFrame.current = window.requestAnimationFrame(movePhotos);
    }, [movePhotos]);

    const getImageOffset = useCallback((index: number) => {
        const imageEl = imagesEl.current?.children.item(index) as HTMLElement | null;
        return imageEl?.offsetTop || imageOffsets[index] || 0;
    }, [imageOffsets]);

    const startMovementToImage = useCallback((imageIndex: number, duration: number) => {
        movementStartOffset.current = currentOffset.current;
        movementEndOffset.current = getImageOffset(imageIndex);
        movementStartTime.current = performance.now();
        movementDuration.current = duration;
    }, [getImageOffset]);

    const getPhotoRevealDuration = useCallback((imageCount: number) => {
        if (!imageCount) {
            return MIN_PHOTO_REVEAL_MS;
        }

        const availableRevealTime = SOUNDTRACK_DURATION_MS - SHOW_START_DELAY_MS - SHOW_END_BUFFER_MS;
        return Math.max(MIN_PHOTO_REVEAL_MS, availableRevealTime / imageCount);
    }, []);

    const playSoundtrack = useCallback((attempt = 1) => {
        if (paused.current) {
            return;
        }

        const soundtrackPlayer = player.current;

        if (!soundtrackPlayer?.playVideo) {
            return;
        }

        try {
            const iframe = soundtrackPlayer.getIframe?.();
            if (!iframe) {
                throw new Error('YouTube iframe is not ready');
            }

            soundtrackPlayer.playVideo();
        } catch (e) {
            if (attempt < SOUNDTRACK_MAX_ATTEMPTS) {
                soundtrackRetryTimer.current = window.setTimeout(() => {
                    playSoundtrack(attempt + 1);
                }, SOUNDTRACK_RETRY_MS);
                return;
            }

            console.warn('Unable to start Big Year soundtrack', e);
        }
    }, []);

    const pauseSoundtrack = useCallback(() => {
        const soundtrackPlayer = player.current;

        if (!soundtrackPlayer?.pauseVideo) {
            return;
        }

        try {
            soundtrackPlayer.pauseVideo();
        } catch (e) {
            console.warn('Unable to pause Big Year soundtrack', e);
        }
    }, []);

    const scheduleReveal = useCallback((imageIndex: number, delay: number) => {
        if (revealTimer.current) {
            window.clearTimeout(revealTimer.current);
        }

        nextRevealIndex.current = imageIndex;
        nextRevealDueAt.current = performance.now() + delay;
        revealTimer.current = window.setTimeout(() => {
            revealImageRef.current(imageIndex);
        }, delay);
    }, []);

    const pauseShow = useCallback(() => {
        if (!started.current || paused.current || complete.current) {
            return;
        }

        const now = performance.now();
        const nextOffset = getCurrentMovementOffset();
        remainingRevealDelay.current = Math.max(0, nextRevealDueAt.current - now);
        remainingMovementDelay.current = Math.max(0, movementDuration.current - (now - movementStartTime.current));
        paused.current = true;
        setIsPaused(true);

        if (revealTimer.current) {
            window.clearTimeout(revealTimer.current);
            revealTimer.current = undefined;
        }

        if (animationFrame.current) {
            window.cancelAnimationFrame(animationFrame.current);
            animationFrame.current = undefined;
        }

        if (soundtrackRetryTimer.current) {
            window.clearTimeout(soundtrackRetryTimer.current);
            soundtrackRetryTimer.current = undefined;
        }

        applyStageOffset(nextOffset);
        pauseSoundtrack();
    }, [applyStageOffset, getCurrentMovementOffset, pauseSoundtrack]);

    const resumeShow = useCallback(() => {
        if (!started.current || !paused.current || complete.current) {
            return;
        }

        paused.current = false;
        setIsPaused(false);
        movementStartOffset.current = currentOffset.current;
        movementStartTime.current = performance.now();
        movementDuration.current = remainingMovementDelay.current;
        startAnimationLoop();
        scheduleReveal(nextRevealIndex.current, remainingRevealDelay.current);
        playSoundtrack();
    }, [playSoundtrack, scheduleReveal, startAnimationLoop]);

    const togglePaused = useCallback(() => {
        if (paused.current) {
            resumeShow();
            return;
        }

        pauseShow();
    }, [pauseShow, resumeShow]);

    const revealImage = useCallback((imageIndex: number) => {
        const nextVisibleIndex = imageIndex + 1;
        const nextImageIndex = imageIndex + 1;

        revealedIndex.current = nextVisibleIndex;
        setCurrentIndex(nextVisibleIndex);

        if (nextImageIndex < images.length) {
            startMovementToImage(nextImageIndex, photoRevealDuration.current);
            scheduleReveal(nextImageIndex, photoRevealDuration.current);
        } else {
            complete.current = true;
        }
    }, [images.length, scheduleReveal, startMovementToImage]);

    useEffect(() => {
        revealImageRef.current = revealImage;
    }, [revealImage]);

    const startShow = useCallback(() => {
        if (started.current || !isPlayerReady || !images.length) {
            return;
        }

        started.current = true;
        setIsLoading(false);

        startAnimationLoop();
        photoRevealDuration.current = getPhotoRevealDuration(images.length);
        scheduleReveal(0, SHOW_START_DELAY_MS);
        playSoundtrack();
    }, [getPhotoRevealDuration, images.length, isPlayerReady, playSoundtrack, scheduleReveal, startAnimationLoop]);

    useEffect(() => {
        startShow();

        return function cleanup() {
            clearTimers();
        }
    }, [clearTimers, startShow]);

    const handlePlayerReady: YouTubeProps['onReady'] = (e) => {
        player.current = e.target;
        setIsPlayerReady(true);
    }

    const getBigYearLabel = () => {
        if (year === 'life') {
            return 'Your Big Life List'
        }

        return `Your ${year} Big Year`;
    }

    const getLabelPosition = (i: number) => {
        return {
            top: `${imageOffsets[i] || getImageOffset(i)}px`
        }
    }

    const getLoadingText = () => {
        if (totalImages) {
            return `Loading photos ${loadedCount}/${totalImages}`;
        }

        return 'Loading photos';
    }

    return (
        <div className="big-year" ref={rootEl}>
            <>
                {isLoading ?
                    <div className="big-year__loading">
                        <div className="big-year__name">{getBigYearLabel()}</div>
                        <div className="big-year__load-anim">{getLoadingText()}&hellip;</div>
                    </div>
                    : null}
                <div className="big-year__scroll">
                    {images.length ?
                        <div className="big-year__images" ref={imagesEl}>
                            {images.map((observation, i) => (
                                <div
                                    className={`big-year__img ${i < currentIndex ? 'state-visible' : ''}`}
                                    key={`${observation.commonName}${i}`}
                                    style={{zIndex: i + 1}}
                                >
                                    <img alt={observation.commonName} src={observation.image} />
                                    <div className="big-year__img-label">{observation.commonName}</div>
                                </div>
                            ))}
                        </div>
                        : null
                    }
                    <div className="big-year__labels" ref={labelsEl}>
                        {labels.map(label => (
                            <div
                                className={`big-year__label ${label[1] < currentIndex ? 'state-visible' : ''}`}
                                key={label[0]}
                                style={getLabelPosition(label[1])}
                            >
                                {label[0]}
                            </div>
                        ))}
                    </div>
                </div>
                <div className="big-year__count">
                    {currentIndex}
                </div>
                {!isLoading ?
                    <PlayControls
                        paused={isPaused}
                        togglePaused={togglePaused}
                        showSkipControls={false}
                    />
                    : null}
                <YouTube
                    videoId={VIDEO_ID}
                    className="big-year__audio"
                    onReady={handlePlayerReady}
                    />
            </>
        </div>
    )
}
